import React, {useEffect, useMemo, useState} from 'react'
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider,
	FormControl,
	FormControlLabel,
	IconButton, InputLabel, Link,
	MenuItem,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow, TextField,
	Tooltip, Typography,
	useTheme,
}                                                                                       from '@mui/material'
import {
	Archive,
	ArchiveOutlined,
	ArrowBack,
	Check,
	Close, MessageRounded,
	Refresh,
	Unarchive, UnarchiveOutlined,
	VideoCameraBack,
	Warning, YouTube
} from "@mui/icons-material"
import {useLocation, useMatch, useNavigate} from "react-router-dom"
import {doGet, doPost}                      from "../../util/do-fetch"
import {logger}              from "../../util/log-utils"
import HoverTableRow         from "../HoverTableRow"
import VideoJobDialog    from "./VideoJobDialog"
import {fmtDttm, fmtPct} from "../../util/prepkit-utils"
import {getSupabase}     from "../../util/supabase-utils"
import {fetchSignedMediaUrl} from "../../util/admin"
import CategoryLabel    from "./CategoryLabel"
import LeftRightCenter from "../LeftRightCenter"
import LeftRight from "../LeftRight"
import {Video} from "@builder.io/react"
import moment                               from "moment"

const log = logger("Videos", 1)

const STATUSES = {
	completed: {name: "Completed", color: "success"},
	assembling: {name: "Assembling", color: "info"},
	generating: {name: "Generating", color: "secondary"},
	processing: {name: "Processing", color: "info"},
	queued: {name: "Queued", color: "warning"},
	failed: {name: "Failed", color: "error"},
}

function Videos() {
	const navigate = useNavigate()
	const theme = useTheme()
	const location = useLocation()
	const [videoJobs, setVideoJobs] = useState([])
	const [keyword, setKeyword] = useState("")
	const [showAspectRatios, setShowAspectRatios] = useState({})
	const [showArchived, setShowArchived] = useState(false)
	const [showQueued, setShowQueued] = useState(true)
	const [showGenerating, setShowGenerating] = useState(true)
	const [showProcessing, setShowProcessing] = useState(true)
	const [showAssembling, setShowAssembling] = useState(true)
	const [showCompleted, setShowCompleted] = useState(true)
	const [showFailed, setShowFailed] = useState(true)
	const [errorDialog, setErrorDialog] = useState({ open: false, message: '' })
	const [categories, setCategories] = useState([])
	const [categoryFilter, setCategoryFilter] = useState('all')

	const isCreateRoute = useMatch('/admin/videos/create')

	const filtered = useMemo(() => {
		if(!videoJobs) {
			return []
		}
		else {
			return videoJobs.filter(vj => {
				const statusMatch =
					(vj.status === 'queued' && showQueued) ||
					(vj.status === 'generating' && showGenerating) ||
					(vj.status === 'processing' && showProcessing) ||
					(vj.status === 'assembling' && showAssembling) ||
					(vj.status === 'completed' && showCompleted) ||
					(vj.status === 'failed' && showFailed)

				const keywordMatch =
					!keyword || vj.title?.toLowerCase().includes(keyword.toLowerCase())

				// allow archived only when showArchived = true
				const archivedMatch =
					!vj.archived_at || showArchived

				// must match selected aspect ratios
				const aspectRatioMatch =
					showAspectRatios[vj.aspect_ratio]

				const categoryMatch =
					categoryFilter === 'all' ||
					(categoryFilter === 'none' && !vj.category_id) ||
					vj.category_id === categoryFilter

				return statusMatch && keywordMatch && archivedMatch && aspectRatioMatch && categoryMatch
			})
		}
	}, [videoJobs, showQueued, showProcessing, showAssembling, showGenerating, showCompleted, showFailed, keyword, showArchived, showAspectRatios, categoryFilter])

	const aspectRatios = useMemo(()=>{
		return videoJobs
			?.map(vj => vj.aspect_ratio)
			.filter((c, i, arr) => arr.findIndex(x => x === c) === i)
	}, [videoJobs])

	useEffect(() => {
		// default to all aspect ratios checked
		const ratioObj = Object.fromEntries(
			aspectRatios.map(ar => [ar, true])
		)

		setShowAspectRatios(ratioObj)
	}, [aspectRatios])

	useEffect(() => {
		const fetchCategories = async () => {
			const { data } = await getSupabase()
				.from('category')
				.select('id, name, color, color2')
				.order('name', { ascending: true })
			if (data) setCategories(data)
		}
		fetchCategories()
	}, [])

	const categoryById = useMemo(() => {
		const map = {}
		for (const cat of categories) map[cat.id] = cat
		return map
	}, [categories])

	useEffect(() => {
		fetchVideoJobs()
		const subscription = subscribeToVideoJobsChannel()
		return () => {
			console.log(`unsubscribing from video-jobs-channel`)
			subscription && subscription.unsubscribe()
		}
	}, [])

	const fetchVideoJobs = async () => {
		try {
			const response = await doGet(`/admin/videos/jobs`)
			const data = await response.json()
			if (Array.isArray(data)) {
				data.sort((a, b) => b.id - a.id)
			}
			setVideoJobs(data)
		}
		catch (err) {
			log.error(err)
		}
	}

	const subscribeToVideoJobsChannel = (job) => {
		const channelName = `channel-video-jobs`
		console.log(`subscribing to ${channelName}`)
		const supabase = getSupabase()
		const ch = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "video_job"
				},
				(event) => {
					log.error(`UPDATE video_job ${event.new.id}`)
					if (event.eventType === "UPDATE") {
						setVideoJobs(prev => {
							return prev.map(vj => {
								if (vj.id === event.new.id) {
									return event.new
								}
								else {
									return vj
								}
							})
						})
					}
					else if (event.eventType === "INSERT") {
						setVideoJobs(prev => {
							// sort by id descending (newest at top)
							return [event.new, ...prev].sort((a, b) => b.id - a.id)
						})
					}
				}
			)
			.subscribe()
		return ch
	}

	const handleSaved = (newVideoJob) => {
		// new video job will be added automatically via realtime subscription
		navigate('/admin/videos')
	}

	const handleRetry = async (videoJob) => {
		await doPost(`/admin/videos/jobs/${videoJob.id}/retry`)
	}

	const handleUnarchive = async (videoJob) => {
		await doPost(`/admin/videos/jobs/${videoJob.id}/unarchive`)
	}

	const handleArchive = async (videoJob) => {
		await doPost(`/admin/videos/jobs/${videoJob.id}/archive`)
	}

	const handleOpenError = (message) => {
		setErrorDialog({ open: true, message })
	}

	const handleCloseError = () => {
		setErrorDialog({ open: false, message: '' })
	}

	return (
		<Container maxWidth="xl">
			<LeftRight
				left={
					<Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
						<IconButton onClick={() => navigate(`/admin`)}>
							<ArrowBack/>
						</IconButton>
						<Typography variant={"h5"}>
							Videos
						</Typography>
					</Box>
				}
				right={
					<Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center">
						<TextField
							sx={{mr: 1}}
							size={"small"}
							placeholder="Filter by Title..."
							value={keyword}
							onChange={e => setKeyword(e.target.value)}
						/>
						<FormControl size="small" sx={{ mr: 1, minWidth: 160 }}>
							<InputLabel>Category</InputLabel>
							<Select
								value={categoryFilter}
								onChange={e => setCategoryFilter(e.target.value)}
								label="Category"
							>
								<MenuItem value="all">All</MenuItem>
								<MenuItem value="none">No Category</MenuItem>
								{categories.map(cat => (
									<MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
								))}
							</Select>
						</FormControl>
						<Button
							variant="contained"
							onClick={() => navigate('/admin/videos/create')}
						>
							Create Video
						</Button>
					</Box>
				}
			/>
			<Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
				<FormControlLabel label="Archived"
				                  control={
					                  <Checkbox checked={showArchived}
					                            onChange={() => setShowArchived(!showArchived)}
					                  />
				                  }
				/>
				{
					aspectRatios.map(ar => {
						return (
							<FormControlLabel
								key={`key-${ar}`}
								label={ar}
				                control={
					                <Checkbox checked={!!showAspectRatios[ar]}
			                          onChange={() => {
				                          setShowAspectRatios(prev => ({
					                          ...prev,
					                          [ar]: !prev[ar]
				                          }))
			                          }}
					                />
				                }
							/>
						)
					})
				}
				<FormControlLabel label="Queued"
				                  control={
					                  <Checkbox checked={showQueued}
					                            onChange={() => setShowQueued(!showQueued)}
					                  />
				                  }
				/>
				<FormControlLabel label="Generating"
				                  control={
					                  <Checkbox checked={showGenerating}
					                            onChange={() => setShowGenerating(!showGenerating)}
					                  />
				                  }
				/>
				<FormControlLabel label="Processing"
				                  control={
					                  <Checkbox checked={showProcessing}
					                            onChange={() => setShowProcessing(!showProcessing)}
					                  />
				                  }
				/>
				<FormControlLabel label="Assembling"
				                  control={
					                  <Checkbox checked={showAssembling}
					                            onChange={() => setShowAssembling(!showAssembling)}
					                  />
				                  }
				/>
				<FormControlLabel label="Completed"
				                  control={
					                  <Checkbox checked={showCompleted}
					                            onChange={() => setShowCompleted(!showCompleted)}
					                  />
				                  }
				/>
				<FormControlLabel label="Failed"
				                  control={
					                  <Checkbox checked={showFailed}
					                            onChange={() => setShowFailed(!showFailed)}
					                  />
				                  }
				/>
			</Box>
			<Stack direction="row" justifyContent="space-between" alignItems="center">



				{
					// conditionally render component so state is reset after close/re-open
					isCreateRoute &&
					<VideoJobDialog
						open={isCreateRoute}
						onCancel={() => navigate('/admin/videos')}
						onSaved={handleSaved}
					/>
				}
			</Stack>
			<Divider sx={{mt: 2}}/>
			<TableContainer style={{ borderRadius: '5px', marginBottom: '125px'}}>
				<Table sx={{backgroundColor: theme.palette.background.paper}} aria-label="simple table">
					<TableHead>
						<TableRow>
							<TableCell sx={{fontWeight: "bold"}} component="th">Format</TableCell>
							<TableCell sx={{fontWeight: "bold"}} component="th">Title</TableCell>
							<TableCell sx={{fontWeight: "bold"}} component="th" align="center">Status</TableCell>
							<TableCell sx={{fontWeight: "bold"}} component="th">Started/Completed</TableCell>
							<TableCell sx={{fontWeight: "bold"}} component="th">Progress</TableCell>
							<TableCell component="th"></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filtered && filtered.map((row) => {
							return <VideoJobTableRow
								key={row.id}
								videoJob={row}
								categoryById={categoryById}
								onRetry={handleRetry}
								onOpenError={handleOpenError}
								onArchive={handleArchive}
								onUnarchive={handleUnarchive}
							/>
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<Dialog open={errorDialog.open} onClose={handleCloseError}>
				<DialogTitle>Message</DialogTitle>
				<DialogContent>
					<DialogContentText>{errorDialog.message}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseError} color="primary">Close</Button>
				</DialogActions>
			</Dialog>
		</Container>
	)
}

function VideoJobTableRow({videoJob, categoryById, onRetry, onOpenError, onArchive, onUnarchive}) {

	const theme = useTheme()
	const navigate = useNavigate()
	const [working, setWorking] = useState(false)
	const archived = useMemo(()=>{
		return videoJob.archived_at
	}, [videoJob.archived_at])
	const generationDuration = useMemo(() => {
		if (!videoJob?.started_at) return ''

		const start = new Date(videoJob.started_at).getTime()
		const end = videoJob.failed_at
			? new Date(videoJob.failed_at).getTime()
			: new Date(videoJob.completed_at).getTime()

		if (!end || end < start) return ''

		const totalSeconds = Math.floor((end - start) / 1000)
		const mins = Math.floor(totalSeconds / 60)
		const secs = totalSeconds % 60

		return `${mins}m ${secs}s`
	}, [videoJob])

	if (!videoJob) return null

	const downloadVideoJobFile = async () => {
		const url = `/admin/videos/jobs/${videoJob.id}/video-url/${videoJob.file_name}`
		const response = await doGet(url)
		const signedUrl = await response.text()
		const link = document.createElement('a')
		link.href = signedUrl
		link.download = videoJob.file_name
		link.target = "_video-job-download"
		//link.innerHTML = "CLICK ME"
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}


	const status = STATUSES[videoJob.status]
	const genPct = videoJob.media_progress_percent &&
		videoJob.media_progress_percent < 100 &&
		` ${videoJob.media_progress_percent}%`
	let statusLabel = status.name

	if(videoJob.status === 'generating' || videoJob.status === 'assembling') {
		statusLabel = <Box>
			<CircularProgress sx={{mr: 0.5, color: "white"}} size={10}/>
			{status.name}{genPct}
		</Box>
	}

	const statusComp = status &&
		<Chip size={"small"} color={status.color} label={statusLabel}/>


	return <HoverTableRow
		key={videoJob.id}
		onClick={() => navigate(`/admin/videos/jobs/${videoJob.id}`)}
		sx={{
			'&:last-child td, &:last-child th': {border: 0},
			cursor: 'pointer',
		}}
	>
		<TableCell component="th" scope="row">
			<b>{videoJob.aspect_ratio}</b>
		</TableCell>
		<TableCell component="th" scope="row">
			<Box whiteSpace={"nowrap"}>
				<b>{videoJob.title}</b>
				{
					videoJob.environment !== 'prod' &&
					<Chip sx={{ml: 1}} label={videoJob.environment} size={"small"} variant={"outlined"} color={"warning"} />
				}
			</Box>
			{categoryById?.[videoJob.category_id] && (
				<CategoryLabel
					variant="caption"
					category={categoryById[videoJob.category_id]}
				/>
			)}
		</TableCell>
		<TableCell align="center">
			{statusComp}
		</TableCell>
		<TableCell>
			<Box whiteSpace={"nowrap"}>
				{
					videoJob.started_at &&
					<Typography color={"text.secondary"}
					            variant={"body2"}
					            fontSize={11}>
						{moment(videoJob.started_at).calendar()}
					</Typography>
				}
			</Box>
			<Box whiteSpace={"nowrap"}>
				{
					videoJob.failed_at
						&& <Box sx={{color: theme.palette.error.main, fontSize: 11}}>Failed {moment(videoJob.failed_at).calendar()}</Box>
				}
				{
					videoJob.completed_at
					&& <Box sx={{fontSize: 11}}>{moment(videoJob.completed_at).calendar()}</Box>
				}
			</Box>
		</TableCell>
		<TableCell sx={{width: "300px"}}>
			{
				videoJob.assembly_status &&
				<Typography color={"text.secondary"}
				            variant={"body2"}
				            fontSize={11}
				>
					{videoJob.assembly_status}
				</Typography>
			}
			{
				<Typography color={"text.secondary"}
				            variant={"body2"}
				            fontSize={11}>
					{moment(videoJob.last_assembly_progress_at).format('MMM D, h:mm:ss a')}
				</Typography>
			}
		</TableCell>
		{/*
		<TableCell>
			{generationDuration}
		</TableCell>
		<TableCell align="center">
			{videoJob.rescue_count}
		</TableCell>
		*/}
		<TableCell>
			<Box display="flex" justifyContent="flex-end" alignItems="center">
				{
					videoJob.message &&
					<IconButton
						onClick={(evt)=> {
							evt.preventDefault()
							evt.stopPropagation()
							onOpenError(videoJob.message)
						}}
						sx={{mt: 0.5}}
					>
						<MessageRounded color={videoJob.failed_at ? "error" : "info"}/>
					</IconButton>
				}
				{
					(working || videoJob.status === "processing")
					&& <CircularProgress size={18}/>
				}
				{
					videoJob.status === 'completed' &&
					<Tooltip title={`Open video ${videoJob.file_name}`}>
						<IconButton
							onClick={(event) => {
								event.stopPropagation()
								event.preventDefault()
								downloadVideoJobFile()
							}}
						>
							<YouTube/>
						</IconButton>
					</Tooltip>

				}
				{
					archived
					? <Tooltip title="Unarchived video">
						<IconButton
							disabled={working}
							color={"warning"}
							onClick={async (event) => {
								event.stopPropagation()
								event.preventDefault()
								setWorking(true)
								await onUnarchive(videoJob)
								setWorking(false)
							}}
						>
							<Archive/>
						</IconButton>
					  </Tooltip>
					: <Tooltip title="Archive video">
						<IconButton
							disabled={working}
							onClick={async (event) => {
								event.stopPropagation()
								event.preventDefault()
								setWorking(true)
								await onArchive(videoJob)
								setWorking(false)
							}}
						>
							<UnarchiveOutlined/>
						</IconButton>
					</Tooltip>
				}

				{
					videoJob.status !== "queued"
					&& <Tooltip title="Regenerate video">
						<IconButton
							disabled={working}
							onClick={async (event) => {
								event.stopPropagation()
								event.preventDefault()
								setWorking(true)
								await onRetry(videoJob)
								setWorking(false)
							}}
						>
							<Refresh/>
						</IconButton>
					</Tooltip>
				}
			</Box>

		</TableCell>

	</HoverTableRow>
}

export default Videos
